// _namespaces.js MUST come before any model/view/collection import — it
// bootstraps the AppointmentBooking.Wizard class and its static namespace
// properties (Views, Models, Collections, Events, Errors) so that sub-modules
// can reference them at module-evaluation time.
import "./_namespaces.js";

import "../../utilities/appointment_date.js";

import "./models/wizard.js";
import "./models/booking.js";
import "./views/wizard.js";
import.meta.glob("./templates/**/*.{js,hbs,jst.ejs,jst.ejs.erb}", { eager: true });
import "virtual:client-templates/app/assets/javascripts/appointment_booking/wizard/templates";

// The Wizard class shell and its static namespace properties (Views, Models,
// Collections, Events, Errors, AnalyticsDetails) are created in
// _namespaces.js. Here we attach the real constructor and instance methods
// to that existing class so that `new AppointmentBooking.Wizard(slotId)`
// works as expected.

const Wizard = AppointmentBooking.Wizard;

Wizard.prototype.constructor = Wizard;

// Attach the real constructor logic as an `_init` method, then override
// the constructor to call it. We can't re-assign the class itself (that
// would break sub-module references), so we patch the prototype.
Object.defineProperty(Wizard.prototype, "_init", {
  value: function _init(slotId, options) {
    let searchParams;
    this.handleRouteToStart = this.handleRouteToStart.bind(this);
    this.handleRouteToStep = this.handleRouteToStep.bind(this);
    this.handleCurrentStepProgressChange = this.handleCurrentStepProgressChange.bind(this);
    this.handleCurrentStepIndexChange = this.handleCurrentStepIndexChange.bind(this);
    this.handleComplete = this.handleComplete.bind(this);
    this.handleError = this.handleError.bind(this);
    this.handleUnload = this.handleUnload.bind(this);
    this.handleViewAnalyticsEvent = this.handleViewAnalyticsEvent.bind(this);
    this.trackStepChange = this.trackStepChange.bind(this);
    this.trackAbandonment = this.trackAbandonment.bind(this);
    this.recordStep = this.recordStep.bind(this);
    this.defaultAnalyticsMetadata = this.defaultAnalyticsMetadata.bind(this);
    if (options == null) {
      options = {};
    }
    slotId = Number(slotId); // Coerce to a number to avoid triggering a change event later when assigning an integer to the model
    options = jQuery.extend(
      {
        rootPath: window.location.pathname.replace(/step\/\d+$/, ""), // Use the current path as the root but remove the current step number if present
        onComplete: this.handleComplete, // A callback that will replace the standard behaviour of redirecting to the completion url. The callback will receive the completion url as the only argument.
      },
      options,
    );

    if (window.URLSearchParams) {
      searchParams = new URLSearchParams(window.location.search);
    } else {
      // IE 11 Compat without needing large polyfill
      searchParams = new Map();
      window.location.search
        .substr(1)
        .split("&")
        .forEach((pair) => {
          const [key, value] = Array.from(pair.split("="));
          return searchParams.set(window.decodeURIComponent(key), window.decodeURIComponent(value));
        });
    }

    const defaults = {
      ehr_appointment_slot_id: slotId,
      state: AppointmentBooking.config.default_state,
      returning_patient: searchParams.get("returning_patient") === "true",
      referring_url: searchParams.get("referring_url"),
      referring_ui_component: searchParams.get("referring_ui_component"),
    };

    this.booking = new this.constructor.Models.Booking(defaults);
    this.model = new this.constructor.Models.Wizard({
      steps: ["Welcome", "Visit", "SignIn", "Patient", "Insurance", "VerifyId", "Confirmation"],
      booking: this.booking,
    });
    this.view = new this.constructor.Views.Wizard({ model: this.model });
    this.router = new Backbone.Router();

    // BEHAVIOUR

    // Error monitoring
    this.constructor.Errors.on("error", this.handleError);

    // Back button support. A page can construct the wizard more than once — the host CMS
    // decides that — and Backbone raises on a second start, so the first one owns the router.
    if (!Backbone.History.started) {
      Backbone.history.start({ pushState: true, root: options.rootPath });
    }

    this.router.route("", "start", this.handleRouteToStart);
    this.router.route("step/:index", "step", this.handleRouteToStep);

    this.model.on("change:currentStepProgress", this.handleCurrentStepProgressChange);
    this.model.on("change:currentStepIndex", this.handleCurrentStepIndexChange);
    this.view.on("complete", options.onComplete);
    this.view.$el.on("appointment-booking-wizard-analytics", this.handleViewAnalyticsEvent);

    window.addEventListener("unload", this.handleUnload);

    // INITIALIZE

    this.recordStep(0); // Always start at the beginning, even when reloading part way through, since we don't restore previous state yet

    // Always track an event for the first page so we set the pageview metadata for the current wizard step. The server can decide whether or not to track the default page view on any page with the booking wizard.
    Backbone.listenToOnce(this.booking, "change:visit_type_id", this.trackStepChange);

    this.$el = this.view.$el;
    this.el = this.view.el;

    try {
      this.view.render();
    } catch (error) {
      console.error("[appointment-booking-wizard] view.render() threw:", error);
    }
    // Finish construction so the view can be added to the DOM and the user can see the error
  },
  writable: true,
  configurable: true,
});

// EVENT HANDLERS

Wizard.prototype.handleRouteToStart = function () {
  if (!this.model.get("completed")) {
    return this.view.navigateTo(0);
  }
};

Wizard.prototype.handleRouteToStep = function (stepNumber) {
  if (!this.model.get("completed")) {
    this.view.navigateTo(stepNumber - 1);
  }
  const index = this.model.get("currentStepIndex");

  // Ensure the url represents the current step in case the wizard did not allow forward progression
  window.history.replaceState(null, null, index ? `${index + 1}` : Backbone.history.root);

  // Erase forward history as best we can to avoid having a bunch of forward history that ends up jumping around on the current page
  window.history.pushState(null, null, index ? `${index + 1}` : Backbone.history.root);
  return window.history.back();
};

Wizard.prototype.handleCurrentStepProgressChange = function (model, currentStepProgress) {
  if (currentStepProgress > 0) {
    return this.trackStepChange();
  }
};

Wizard.prototype.handleCurrentStepIndexChange = function (model, currentStepIndex) {
  this.recordStep(currentStepIndex);
  return this.trackStepChange();
};

Wizard.prototype.handleComplete = (appointmentUrl) => (window.location = appointmentUrl);

Wizard.prototype.handleError = function (message) {
  const data = this.model.toJSON();
  data.errorMessage = message;
  data.I18n = AppointmentBooking.config.i18n.wizard;
  data.testIds = AppointmentBooking.config.test_ids;
  return this.$el.html(JST["appointment_booking/wizard/templates/error"](data));
};

Wizard.prototype.handleUnload = function () {
  if (!this.model.get("completed")) {
    return this.trackAbandonment();
  }
};

Wizard.prototype.handleViewAnalyticsEvent = function (event, eventName) {
  // Inject additional metadata into any analytics coming out of the view
  const detail = _.defaults(this.defaultAnalyticsMetadata(), event.detail);
  const newEvent = jQuery.Event(`${eventName}.sparkle`, { detail });
  return jQuery(event.target).trigger(newEvent);
};

// PRIVATE

Wizard.prototype.trackStepChange = function () {
  const event = jQuery.Event("pageview.sparkle", { detail: this.defaultAnalyticsMetadata() });
  return jQuery(document).trigger(event);
};

Wizard.prototype.trackAbandonment = function () {
  const step = this.view.getCurrentStep();
  const metadata = {
    properties: {
      invalidFields: step.getInvalidFields().map((input) => jQuery(input).attr("name")),
    },
  };

  return __guard__(typeof Sparkle !== "undefined" && Sparkle !== null ? Sparkle.Analytics : undefined, (x) =>
    x.trackAbandonment(_.defaults(this.defaultAnalyticsMetadata(), metadata)),
  );
};

Wizard.prototype.recordStep = function (stepIndex, options) {
  let url = "";
  if (stepIndex > 0) {
    url = `${url}/step/${stepIndex + 1}`;
  }
  url = `${url}${window.location.search}`;

  return this.router.navigate(url, options);
};

Wizard.prototype.defaultAnalyticsMetadata = function () {
  const currentStepIndex = this.model.get("currentStepIndex");

  return _.defaults({}, this.constructor.AnalyticsDetails, {
    record_type: "EHRAppointmentSlot",
    record_id: this.booking.get("ehr_appointment_slot_id"),
    step_number: currentStepIndex + 1,
    step_name: this.view.getStep(currentStepIndex).name,
    login_type: this.booking.get("login_type"),
    referring_ui_component: this.booking.get("referring_ui_component"),

    decision_tree_id: this.booking.get("decision_tree_id"),
    decision_tree_current_node_id: this.booking.get("decision_tree_current_node_id"),
    decision_tree_result: this.booking.get("decision_tree_result"),

    slot_suggester_shown: this.booking.get("slot_suggester_shown"),
    slot_suggester_success: this.booking.get("slot_suggester_success"),
    reselection_reason: this.booking.get("reselection_reason"),
    reselection_result: this.booking.get("reselection_result"),
    reselection_type: this.booking.get("reselection_type"),

    valid: this.booking.get("validated_valid"),
    errors: this.booking.get("errors"),
  });
};

function __guard__(value, transform) {
  return typeof value !== "undefined" && value !== null ? transform(value) : undefined;
}
